export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      // The result is a base64 encoded string
      const result = reader.result as string;
      const base64String = result.split(",")[1];

      resolve(base64String);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    // Read the file as a data URL
    reader.readAsDataURL(file);
  });
};

// DD-MM-YYYY format
export const today = new Date().toISOString().split("T")[0];

// debounce function
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const getItemFromLocalStore = (key: string) =>
  JSON.parse(localStorage.getItem(key)!);

export function getDaysBetweenDates(
  date1: string | Date,
  date2: string | Date,
): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Validate if the dates are valid
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    throw new Error("Invalid date(s) provided");
  }

  // Calculate the difference in milliseconds
  const diffInMilliseconds = Math.abs(d2.getTime() - d1.getTime());

  // Convert milliseconds to days (1 day = 24 * 60 * 60 * 1000 milliseconds)
  const days = diffInMilliseconds / (1000 * 60 * 60 * 24);

  return days;
}

// DD-MM-YYYY format from a timestamp
export function formattedDate(date: string) {
  const d = new Date(date);

  return `${d.getMonth() + 1}-${d.getDay()}-${d.getFullYear()}`;
}

export function calculatePercentage(part: number, total: number): number {
  return parseFloat(((part / 100) * total).toFixed(2));
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return str; // Handle empty strings
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getTomorrow() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const formatted = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata", // Explicitly set IST
  }).format(tomorrow);

  return formatted;
}

// convert string to snake case
export function toSnakeKey(input: string): string {
  return input.trim().replace(/\s+/g, "_").toLowerCase();
}

// utils timeAgo
export function timeAgo(date: string | Date) {
  const d = new Date(date); // this parses ISO/UTC correctly
  const now = new Date();

  const seconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function parseServerDate(dateStr: string) {
  // append "Z" to treat it as UTC
  return new Date(dateStr.replace(" ", "T") + "Z");
}

// utils -- throttle
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number,
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let inThrottle = false;
  let lastArgs: Parameters<T> | null = null;
  let lastContext: any;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const throttled = function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;

      timeoutId = setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func.apply(lastContext, lastArgs);
          lastArgs = null;
          lastContext = null;
        }
      }, limit);
    } else {
      lastArgs = args;
      lastContext = this;
    }
  };

  throttled.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    inThrottle = false;
    lastArgs = null;
    lastContext = null;
  };

  return throttled;
}

/**
 * Generates a consistent color based on a string.
 *
 * The function hashes the input string into a number,
 * then maps that number to one of the predefined colors.
 * This ensures the same input always returns the same color.
 */
export function getStringColor(input: string) {
  const colors = [
    "#2563EB",
    "#D97706",
    "#DC2626",
    "#059669",
    "#7C3AED",
    "#DB2777",
  ];

  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

export const parseDateSafe = (value: string): Date | null => {
  if (!value) return null;

  const v = value.trim();
  const now = new Date();

  // ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    const iso = new Date(v);
    if (!isNaN(iso.getTime())) return iso;
  }

  // D/M/YYYY or DD/MM/YYYY with any separator
  let match = v.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    return new Date(+y, +m - 1, +d);
  }

  // D/M → assume current year
  match = v.match(/^(\d{1,2})[\/\-.](\d{1,2})$/);
  if (match) {
    const [, d, m] = match;
    return new Date(now.getFullYear(), +m - 1, +d);
  }

  // M/YYYY
  match = v.match(/^(\d{1,2})[\/\-.](\d{4})$/);
  if (match) {
    const [, m, y] = match;
    return new Date(+y, +m - 1, 1);
  }

  // YYYY/M
  match = v.match(/^(\d{4})[\/\-.](\d{1,2})$/);
  if (match) {
    const [, y, m] = match;
    return new Date(+y, +m - 1, 1);
  }

  // Native parser (handles: 19 JAN 2026, Jan 19, 2026, January 19, 2026)
  const native = new Date(v);
  if (!isNaN(native.getTime())) return native;

  return null;
};

export function formatLocalDate(date: Date | null): string {
  if (!date) return "";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

// calculate estimate quotes price range
export function calculatePriceRange(
  price: number,
  options: { minus?: number; plus?: number } = {},
) {
  const { minus = 20, plus = 10 } = options;

  const min = price * (1 - minus / 100);
  const max = price * (1 + plus / 100);

  const formatted = (currency = "$") =>
    `${currency}${min.toFixed(2)} - ${currency}${max.toFixed(2)}`;

  return { min, max, formatted };
}
